import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { filterNonDisplayableGroupNames, invariant } from '@streem/toolbox';
import { AppIcon, Theme, useSnackbar } from '@streem/ui-react';
import { useCompanyInfo } from '../context/company_info';
import { useGroups } from '../context/groups';
import { useReservation } from '../context/reservation';
import { useRoomSession } from '../context/room_session';
import { convertGroupToTrade } from '../util/convert_group_to_trade';
import { useObservable } from './rxjs';
import { useHoursOfOperation } from '../context/hours_of_operation';
import { sortTradesByName } from '../util/sort_trades_by_name';
import { streem } from 'streem-sdk-protobuf';
const REQUEUE_SNACKBAR_ID = 'requeue-snackbar';
export const ReservationType = streem.api.GroupReservation.ReservationType;
export function useCallTransferButton() {
    const groups = useGroups();
    const { reservation } = useReservation();
    const roomSession = useRoomSession();
    const { companyCode } = useCompanyInfo() ?? {};
    const hoursOfOperation = useHoursOfOperation();
    const isRequeue = reservation?.reservationType === ReservationType.RESERVATION_TYPE_REQUEUE;
    const { snackbarNotifications, clearRequeueDismissed } = useRequeueFailureSnackbar(roomSession);
    const trades = useMemo(() => {
        const trades = filterNonDisplayableGroupNames(groups).map(group => {
            return convertGroupToTrade(group, reservation, hoursOfOperation);
        });
        return sortTradesByName(trades);
    }, [groups, reservation, hoursOfOperation]);
    const onRequeueTrade = useCallback(async (newGroupName) => {
        const { group, reservationSid } = reservation ?? {};
        const groupName = group?.name;
        invariant(reservation && group && reservationSid && groupName, `group, reservationSid, and group.name must exist on reservation: ${reservation}`);
        invariant(companyCode, 'companyCode must be set');
        clearRequeueDismissed();
        await roomSession.requeue(companyCode, groupName, reservationSid, newGroupName);
    }, [companyCode, reservation, roomSession, clearRequeueDismissed]);
    const disabled = useMemo(() => {
        const atLeastOneTradeEnabled = trades.some(t => !t.disableTransfer);
        return trades.length === 0 || !atLeastOneTradeEnabled || isRequeue;
    }, [trades, isRequeue]);
    return {
        trades,
        onRequeueTrade,
        disabled,
        snackbarNotifications,
    };
}
// exported for testing
export function useRequeueFailureSnackbar(roomSession) {
    const { snackbarNotifications, addNotification, removeNotification } = useSnackbar();
    const [requeueFailure] = useObservable(roomSession.requeueFailure);
    const [requeueFailureMessageDismissed, setRequeueFailureMessageDismissed] = useState(false);
    const dismissRequeueFailure = useCallback(function () {
        setRequeueFailureMessageDismissed(true);
    }, []);
    const clearRequeueDismissed = useCallback(function () {
        setRequeueFailureMessageDismissed(false);
    }, []);
    useEffect(() => {
        const notificationAlreadyAdded = snackbarNotifications.some(({ id }) => id === REQUEUE_SNACKBAR_ID);
        const addNotificationYn = requeueFailure && !notificationAlreadyAdded && !requeueFailureMessageDismissed;
        const removeNotificationYn = notificationAlreadyAdded &&
            (requeueFailure === false || requeueFailureMessageDismissed);
        if (addNotificationYn) {
            addNotification({
                id: REQUEUE_SNACKBAR_ID,
                message: 'Could not transfer video chat, try again',
                icon: _jsx(AppIcon, { name: "AlertIcon", color: Theme.colors.red50 }),
                useCloseButton: true,
                onActionClick: dismissRequeueFailure,
            });
        }
        if (removeNotificationYn) {
            removeNotification(REQUEUE_SNACKBAR_ID);
        }
    }, [
        addNotification,
        dismissRequeueFailure,
        clearRequeueDismissed,
        removeNotification,
        requeueFailure,
        snackbarNotifications,
        requeueFailureMessageDismissed,
    ]);
    return {
        clearRequeueDismissed,
        snackbarNotifications,
        dismissRequeueFailure,
    };
}
//# sourceMappingURL=use_call_transfer_button.js.map